import React, { useEffect, useRef, useState } from "react"
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { AIRLINES } from "data/airlines"
import { isFlightValid, todayLocal } from "utils/flightLogic"

const EMPTY = {
  who: "",
  airline: "",
  airlineCode: "",
  flightNumber: "",
  date: "",
  origin: "",
  destination: "",
  note: "",
}

function FlightFormDialog({ open, initialFlight, onClose, onSubmit }) {
  const [values, setValues] = useState(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"))

  // Provenance of the current airlineCode. True only while the code is the one
  // a catalog selection auto-filled. Typing the airline away from that entry
  // must clear such a code -- otherwise the card would display one carrier and
  // link to another, since buildFlightSearchUrl prefers the code. A code the
  // user typed into the Code field themselves is never auto-cleared.
  const codeFromCatalog = useRef(false)

  useEffect(() => {
    if (!open) return
    const next = initialFlight
      ? { ...EMPTY, ...initialFlight }
      : { ...EMPTY, date: todayLocal() }
    setValues(next)
    // A saved flight whose name/code pair matches the catalog is treated as
    // catalog-derived, so editing the carrier on an existing flight is covered.
    codeFromCatalog.current = AIRLINES.some(
      (a) => a.name === next.airline && a.code === next.airlineCode
    )
    setSubmitting(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFlight?.id])

  const set = (field) => (event) =>
    setValues((prev) => ({ ...prev, [field]: event.target.value }))

  // Hand-editing the Code field makes the code the user's own; from then on
  // airline text edits leave it alone.
  const handleCodeChange = (event) => {
    codeFromCatalog.current = false
    setValues((prev) => ({ ...prev, airlineCode: event.target.value }))
  }

  const applyAirlineText = (text) => {
    const inCatalog = AIRLINES.some((a) => a.name === text)
    const dropCode = !inCatalog && codeFromCatalog.current
    if (dropCode) codeFromCatalog.current = false
    setValues((prev) => ({
      ...prev,
      airline: text,
      airlineCode: dropCode ? "" : prev.airlineCode,
    }))
  }

  const handleAirlineChange = (_event, next, reason) => {
    if (next && typeof next === "object") {
      codeFromCatalog.current = true
      setValues((prev) => ({
        ...prev,
        airline: next.name,
        airlineCode: next.code,
      }))
    } else if (reason === "clear") {
      // The clear "x" must reset both fields so they cannot disagree.
      codeFromCatalog.current = false
      setValues((prev) => ({ ...prev, airline: "", airlineCode: "" }))
    } else {
      // Free-text carrier committed on blur/Enter.
      applyAirlineText(next || "")
    }
  }

  const handleSubmit = () => {
    // Guards against a double tap creating two documents.
    if (submitting || !isFlightValid(values)) return
    setSubmitting(true)
    onSubmit(values)
    onClose()
  }

  const airlineValue =
    AIRLINES.find((a) => a.name === values.airline) || values.airline

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ py: { xs: 1.5, sm: 2 } }}>
        {initialFlight ? "Edit flight" : "Add flight"}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: "grid", gap: { xs: 1.75, sm: 2 }, pt: 1 }}>
          <TextField
            label="Who's flying"
            value={values.who}
            onChange={set("who")}
            fullWidth
            required
            autoFocus
          />

          <Autocomplete
            freeSolo
            options={AIRLINES}
            value={airlineValue}
            onChange={handleAirlineChange}
            onInputChange={(_event, next, reason) => {
              if (reason === "input") applyAirlineText(next)
            }}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${option.name} (${option.code})`
            }
            isOptionEqualToValue={(option, value) =>
              option.name === (typeof value === "string" ? value : value?.name)
            }
            renderInput={(params) => (
              <TextField {...params} label="Airline" required />
            )}
          />

          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 1.5 }}
          >
            <TextField
              label="Code"
              value={values.airlineCode}
              onChange={handleCodeChange}
              placeholder="DL"
              inputProps={{ maxLength: 3 }}
              helperText="IATA"
            />
            <TextField
              label="Flight number"
              value={values.flightNumber}
              onChange={set("flightNumber")}
              placeholder="1234"
              required
              inputMode="numeric"
            />
          </Box>

          <TextField
            label="Date"
            type="date"
            value={values.date}
            onChange={set("date")}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          <Box
            sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
          >
            <TextField
              label="From"
              value={values.origin}
              onChange={set("origin")}
              placeholder="SLC"
              inputProps={{ maxLength: 4 }}
            />
            <TextField
              label="To"
              value={values.destination}
              onChange={set("destination")}
              placeholder="ATL"
              inputProps={{ maxLength: 4 }}
            />
          </Box>

          <TextField
            label="Note"
            value={values.note}
            onChange={set("note")}
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
        <Button onClick={onClose} sx={{ minHeight: 44 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !isFlightValid(values)}
          sx={{ minHeight: 44 }}
        >
          {initialFlight ? "Save" : "Add flight"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FlightFormDialog
